/* The intake capability record — designs/intake-data-model.md IDM1–IDM4,
   cut to ONE form by designs/intake-unified-form.md UF1/UF2/UF4. The
   prototype's Feature record re-expressed append-only; vocabularies ported
   verbatim from perception-intake/src/domain/enums.ts (IDM3): the form, the
   API validation, and the dashboard filters can never drift. NULL means NOT
   RECORDED; the vocabularies carry their own honest "unknown / unsure /
   none" options, so requiring a vocabulary field never fabricates. */
import type { Stamp, UserRef } from "./organizer-roles";
import { ENTERED_SRC } from "./projects";
import type { CanonicalTeam } from "./canonical-teams";

/* ── controlled vocabularies (IDM3) ── */

export interface VocabOption {
  value: string;
  label: string;
}

export const SCOPES = [
  { value: "common", label: "Likely common to several vehicles" },
  { value: "specific", label: "Vehicle-specific" },
  { value: "unsure", label: "Not sure — flag for coordination" },
] as const satisfies readonly VocabOption[];

export const SUB_STATUSES = [
  { value: "done", label: "Done / Demonstrated" },
  { value: "in_progress", label: "In progress" },
  { value: "planned", label: "Planned" },
  { value: "needed", label: "Needed — not started" },
  { value: "unknown", label: "Unknown" },
] as const satisfies readonly VocabOption[];

export const PRIORITIES = [
  { value: "must", label: "Must-have" },
  { value: "should", label: "Should-have" },
  { value: "could", label: "Could-have" },
  { value: "wont", label: "Won't this cycle" },
] as const satisfies readonly VocabOption[];

export const PHASE_TARGETS = [
  { value: "p3", label: "Oct 15 / Phase 3 exit" },
  { value: "p4", label: "Phase 4" },
  { value: "p5", label: "Phase 5" },
  { value: "prod", label: "Productization" },
  { value: "future", label: "Future / roadmap (no gate yet)" },
] as const satisfies readonly VocabOption[];

export const JAMA_STATUSES = [
  { value: "linked", label: "Yes — linked" },
  { value: "drafted", label: "Drafted (not linked)" },
  { value: "none", label: "None yet" },
  { value: "na", label: "N/A" },
] as const satisfies readonly VocabOption[];

/* ── who is expected to DELIVER the ask (designs/intake-ownership-model.md
   OM1/OM2) — a SET of canonical teams, not a category ──

   Named teams, because the whole point is spotting overlap: "platform owns
   it" cannot be aggregated, "Perception Platform owns it" can. Ownership is
   routinely spread — the platform team, the requesting team, a third team
   that is neither, or any combination — so this is a list, and "shared"
   stops being a value: it is simply more than one owner, which is more
   honest because it says shared WITH WHOM.

   The three honest states ride on null-vs-empty:
     null / absent  →  NOT RECORDED. Nobody was asked. (red)
     []             →  NOT YET DECIDED. They looked and have not settled.
     [ids…]         →  recorded.
   Nothing is ever defaulted to the platform team — that guess would tell a
   manager platform had committed to work nobody agreed to.

   Deliberately NOT related to MOBIUS_OWNERSHIP, which asks which side of the
   platform / market-plugin CODE boundary an ask sits on (OM4). ── */

/* how many owners a single ask may name — a guard against a paste, not a
   real limit; 20 canonical teams exist and naming them all is not an answer */
const MAX_OWNERS = 12;

export const ownersReason = (owners: unknown, teams: CanonicalTeam[]): string | null => {
  if (owners === null || owners === undefined) return null; /* not recorded */
  if (!Array.isArray(owners)) return "owners must be a list of team ids or null";
  if (owners.length > MAX_OWNERS) return `an ask names at most ${MAX_OWNERS} owning teams`;
  if (new Set(owners).size !== owners.length) return "owners repeats a team";
  for (const id of owners) {
    const team = teams.find((t) => t.id === id);
    if (!team) return `unknown owning team "${String(id)}" — pick from the canonical list`;
    if (team.retired) return `owning team "${team.name}" is retired — pick a current team`;
  }
  return null;
};

export function labelFor(
  options: ReadonlyArray<VocabOption>,
  value: string | null | undefined,
): string {
  if (!value) return "—";
  return options.find((o) => o.value === value)?.label ?? value;
}

const has = (options: ReadonlyArray<VocabOption>, value: unknown): boolean =>
  typeof value === "string" && options.some((o) => o.value === value);

/* ── the record (IDM1) ── */

/* a person on the record (intake-unified-form.md UF16/UF17) — the Graph
   object id the roster carries, plus the name as it read when picked */
export interface PersonRef {
  userId: string;
  name: string;
}

export interface CapabilityInput {
  /* canonical team slug (canonical-teams.md) — who is asking */
  teamId: string;
  name: string;
  description: string | null;
  /* the lane lead running the effort, from any team (UF16): who platform
     calls when another team needs the same thing. Required on a new
     effort; null only on rows filed before it existed — not recorded */
  pointOfContact: PersonRef | null;
  /* which product / vehicle the effort is for (UF21) — a slug from the
     product list, picked or added on the form. OPTIONAL like `owners`: a
     row stored before the field existed reads back absent — not recorded */
  productId?: string | null;
  /* which of that product's vehicles (teams-and-products.md TP4) — OPTIONAL
     and absent-is-null, so a caller written before it existed still files;
     it must belong to `productId` (the route checks) */
  vehicleId?: string | null;
  scope: string;
  subStatus: string;
  /* groom-side since UF13: the requester never picks it; null = not yet
     prioritised. computeScore treats null as the neutral value. */
  priority: string | null;
  neededBy: string | null;
  /* both left off the one form (UF14); null = not recorded */
  phaseTarget: string | null;
  jamaStatus: string | null;
  jamaLink: string | null;
  dependencies: string | null;
  /* which teams are expected to deliver this (OM1/OM2). OPTIONAL, following
     the `cv?:` idiom (IC9): an ask stored before the question existed reads
     back absent — honestly not recorded, never back-filled to a guess.
     `[]` is DIFFERENT: it means the owners were discussed and not settled. */
  owners?: string[] | null;
}

export interface Capability extends CapabilityInput {
  /* filed under the per-domain tabs that existed before Sep 14 2026
     (intake-unified-form.md UF1). READ-ONLY legacy: present on old rows,
     absent on new ones, never set or cleared by an edit, never back-filled.
     The ids are the recorded fact; the taxonomy they named is deleted. */
  domain?: string | null;
  area?: string | null;
  domainDetail?: Record<string, unknown> | null;
  id: string;
  /* out of the queue, never deleted — absent means live */
  archived?: boolean;
  recorded: Stamp;
}

/* a partial correction, Admin-only for now (IDM4); appended, never in place */
export type CapabilityEdit = Partial<CapabilityInput> & { archived?: boolean };

const NAME_MAX = 200;
const TEXT_MAX = 4000;
const LINK_MAX = 500;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

const textReason = (label: string, value: unknown, max: number): string | null => {
  if (value === null) return null;
  if (typeof value !== "string" || !value.trim()) return `${label} must be text or null`;
  return value.length > max ? `${label} is over ${max} characters` : null;
};

/* the EARLIER question set's rules, unchanged (IC9) — records captured
   under it must keep validating exactly as they always did */
export const personReason = (label: string, p: unknown): string | null => {
  if (p === null || p === undefined) return null;
  if (typeof p !== "object") return `${label} must be a person or null`;
  const { userId, name } = p as Record<string, unknown>;
  if (typeof userId !== "string" || !userId.trim()) return `${label} needs a userId`;
  if (typeof name !== "string" || !name.trim()) return `${label} needs a name`;
  if (name.length > NAME_MAX) return `${label} name is over ${NAME_MAX} characters`;
  return null;
};

export function validateCapabilityInput(
  input: CapabilityInput,
  teams: CanonicalTeam[],
  /* a NEW effort must name its point of contact (UF16); a correction to a
     row filed before the field existed may leave it honestly null */
  opts: { requireContact?: boolean } = {},
): string | null {
  const team = teams.find((t) => t.id === input.teamId);
  if (!team) return `unknown team "${input.teamId}" — pick from the canonical list`;
  if (team.retired) return `team "${team.name}" is retired — pick a current team`;
  if (typeof input.name !== "string" || !input.name.trim()) return "name is required";
  if (input.name.length > NAME_MAX) return `name is over ${NAME_MAX} characters`;
  if (!has(SCOPES, input.scope)) return "unknown scope";
  if (!has(SUB_STATUSES, input.subStatus)) return "unknown subStatus";
  if (input.priority !== null && !has(PRIORITIES, input.priority)) return "unknown priority";
  if (input.phaseTarget !== null && !has(PHASE_TARGETS, input.phaseTarget))
    return "unknown phaseTarget";
  if (input.jamaStatus !== null && !has(JAMA_STATUSES, input.jamaStatus)) return "unknown jamaStatus";
  const contact = personReason("pointOfContact", input.pointOfContact);
  if (contact) return contact;
  if (input.productId != null && (typeof input.productId !== "string" || !input.productId.trim()))
    return "productId must be a product id or null";
  if (input.vehicleId != null) {
    if (typeof input.vehicleId !== "string" || !input.vehicleId.trim())
      return "vehicleId must be a vehicle id or null";
    if (input.productId == null) return "a vehicle needs its product — send productId too";
  }
  if (opts.requireContact && input.pointOfContact == null)
    return "pointOfContact is required — who is the lane lead on this effort?";
  if (input.neededBy !== null && !DATE_RE.test(String(input.neededBy)))
    return "neededBy must be a YYYY-MM-DD date or null";
  const owners = ownersReason(input.owners, teams);
  if (owners) return owners;
  /* no domain, area, or detail block (UF2): a stale client still sending
     them is not refused — the cleaners drop what the whitelist does not name */
  return (
    textReason("description", input.description, TEXT_MAX) ??
    textReason("jamaLink", input.jamaLink, LINK_MAX) ??
    textReason("dependencies", input.dependencies, TEXT_MAX)
  );
}


/* ── the admin tracking stream (intake-data-model.md IDM8, PR-6) — the
   prototype's PlatformTracking, the grooming side of the authority
   boundary. NO tracking revisions at all = honestly not yet triaged. ── */

export const PLATFORM_STATUSES = [
  { value: "triage", label: "Triage" },
  { value: "accepted", label: "Accepted" },
  { value: "in_progress", label: "In progress" },
  { value: "on_hold", label: "On hold" },
  { value: "blocked", label: "Blocked" },
  { value: "delivered", label: "Delivered" },
  { value: "rejected", label: "Rejected" },
  { value: "duplicate", label: "Duplicate" },
] as const satisfies readonly VocabOption[];

export const EFFORTS = [
  { value: "s", label: "S — days" },
  { value: "m", label: "M — 1–2 weeks" },
  { value: "l", label: "L — several weeks" },
  { value: "xl", label: "XL — a quarter or more" },
] as const satisfies readonly VocabOption[];

export const HEALTHS = [
  { value: "green", label: "Green — on track" },
  { value: "amber", label: "Amber — at risk" },
  { value: "red", label: "Red — off track" },
] as const satisfies readonly VocabOption[];

/* exported so the client can pre-check and skip a doomed round-trip (D8
   idiom) — one message, never two copies to drift apart */
export const UNGROOMED_DISPOSITION_REASON =
  "an ungroomed effort can't be given an outcome — groom it first";

/* intake-flow-review.md FR3: a rejection says why, not only who and when */
export const REJECTION_REASON_REQUIRED =
  "a rejected effort needs a reason — say why in the platform notes";

/* what became of a groomed ask (intake-project-mapping.md IPM2, per Lewis
   #hermes-dev 17015). Paired with projectId — both set or both null
   (IPM3). No "feature" option: the project-vs-feature threshold is a
   separate, undecided sync task. */
export const DISPOSITIONS = [
  { value: "new_project", label: "New project" },
  { value: "covered", label: "Covered by an existing project — watching" },
  { value: "expand_scope", label: "Expands an existing project's scope" },
  /* the register's three outcomes (intake-unified-form.md UF18) */
  { value: "platform_takes_on", label: "Platform takes it on — another team needs it too" },
  { value: "team_builds_platform_reviews", label: "Team builds it; platform reviews" },
  { value: "duplicate_of", label: "Duplicate of another effort" },
] as const satisfies readonly VocabOption[];

/* the outcomes that pair with a project (IPM3) — duplicate_of pairs with
   another EFFORT instead */
export const PROJECT_DISPOSITIONS: readonly string[] = [
  "new_project",
  "covered",
  "expand_scope",
  "platform_takes_on",
  "team_builds_platform_reviews",
];

export const REVIEWER_REQUIRED_REASON =
  '"Team builds it; platform reviews" needs a named platform reviewer';

export interface CapabilityTracking {
  platformStatus: string | null;
  /* the chain's leading stage + its artifact (IDM9): the charter document */
  dcCharter: boolean;
  charterLink: string | null;
  roadmapPhase: string | null;
  platformOwner: string | null;
  teamOwner: string | null;
  plannedStart: string | null;
  plannedEnd: string | null;
  jiraEpic: string | null;
  groomed: boolean;
  effort: string | null;
  committed: boolean;
  dcReqDrafted: boolean;
  dcReqLinked: boolean;
  dcCode: boolean;
  dcTestCases: boolean;
  dcTestArtifacts: boolean;
  dcVerified: boolean;
  health: string | null;
  platformNotes: string | null;
  /* what became of this ask (IPM1) — paired with projectId, never one
     without the other (IPM3) */
  disposition: string | null;
  projectId: string | null;
  /* the effort this one duplicates (UF18/UF19) — set only with
     disposition "duplicate_of"; Admin-only, like the archive it implies */
  duplicateOfId: string | null;
  /* the platform person in the team's designs and reviews (UF17) —
     required for "team_builds_platform_reviews", optional otherwise */
  reviewer: PersonRef | null;
  recorded: Stamp;
}

export type TrackingPatch = Partial<Omit<CapabilityTracking, "recorded">>;

/* the first patch lands onto this — every slot honestly empty */
export const EMPTY_TRACKING: Omit<CapabilityTracking, "recorded"> = {
  platformStatus: null,
  dcCharter: false,
  charterLink: null,
  roadmapPhase: null,
  platformOwner: null,
  teamOwner: null,
  plannedStart: null,
  plannedEnd: null,
  jiraEpic: null,
  groomed: false,
  effort: null,
  committed: false,
  dcReqDrafted: false,
  dcReqLinked: false,
  dcCode: false,
  dcTestCases: false,
  dcTestArtifacts: false,
  dcVerified: false,
  health: null,
  platformNotes: null,
  disposition: null,
  projectId: null,
  duplicateOfId: null,
  reviewer: null,
};

const TRACKING_TEXT_KEYS = [
  "charterLink",
  "roadmapPhase",
  "platformOwner",
  "teamOwner",
  "jiraEpic",
  "platformNotes",
] as const;
const TRACKING_BOOL_KEYS = [
  "dcCharter",
  "groomed",
  "committed",
  "dcReqDrafted",
  "dcReqLinked",
  "dcCode",
  "dcTestCases",
  "dcTestArtifacts",
  "dcVerified",
] as const;
const TRACKING_KEYS = [
  "platformStatus",
  "plannedStart",
  "plannedEnd",
  "effort",
  "health",
  "disposition",
  "projectId",
  "duplicateOfId",
  "reviewer",
  ...TRACKING_TEXT_KEYS,
  ...TRACKING_BOOL_KEYS,
] as const;

/* the fields exclusively edited by the grooming editor (GroomEditor.tsx,
   in the effort's drawer — intake-groom.md IG8) — nowhere
   else writes platformStatus/effort/health/committed/platformNotes.
   Touching any of them marks the ask groomed automatically (per Lewis,
   Sep 2026): grooming is both a manual flag AND an inferred consequence
   of actually grooming something. */
const GROOMING_FIELDS = ["platformStatus", "effort", "health", "committed", "platformNotes"] as const;

/* same lesson as the capability edit: whitelist, drop undefined, trim */
export function cleanTrackingPatch(patch: TrackingPatch): TrackingPatch {
  const out: Record<string, unknown> = {};
  for (const key of TRACKING_KEYS) {
    const value = (patch as Record<string, unknown>)[key];
    if (value !== undefined) out[key] = value;
  }
  for (const key of TRACKING_TEXT_KEYS)
    if (typeof out[key] === "string") out[key] = (out[key] as string).trim();
  /* auto-groom: the admin's own explicit `groomed` in THIS patch always
     wins (manual control stays); otherwise touching a grooming field
     infers it */
  if (out.groomed === undefined && GROOMING_FIELDS.some((key) => out[key] !== undefined))
    out.groomed = true;
  return out as TrackingPatch;
}

/* validates the PATCH's own shape, then — since the pairing rule (IPM3) is
   a cross-field invariant a lone patch can't decide — the MERGED record
   against `current` (the IDM4-a idiom: server is the authority, but the
   authority has to see the whole record, not just what changed). */
export function validateTrackingPatch(
  patch: TrackingPatch,
  current?: CapabilityTracking | null,
): string | null {
  const cleaned = cleanTrackingPatch(patch);
  if (Object.keys(cleaned).length === 0) return "nothing to change";
  /* the charter link becomes a clickable anchor (IDM9) — only http(s) may
     ever be stored, so a javascript: scheme can never reach a href */
  if (
    cleaned.charterLink !== undefined &&
    cleaned.charterLink !== null &&
    !/^https?:\/\//i.test(cleaned.charterLink)
  )
    return "charterLink must be an http(s) URL";
  if (cleaned.platformStatus !== undefined && cleaned.platformStatus !== null && !has(PLATFORM_STATUSES, cleaned.platformStatus))
    return "unknown platformStatus";
  if (cleaned.effort !== undefined && cleaned.effort !== null && !has(EFFORTS, cleaned.effort))
    return "unknown effort";
  if (cleaned.health !== undefined && cleaned.health !== null && !has(HEALTHS, cleaned.health))
    return "unknown health";
  if (cleaned.disposition !== undefined && cleaned.disposition !== null && !has(DISPOSITIONS, cleaned.disposition))
    return "unknown disposition";
  if (
    cleaned.projectId !== undefined &&
    cleaned.projectId !== null &&
    typeof cleaned.projectId !== "string"
  )
    return "projectId must be an id or null";
  if (
    cleaned.duplicateOfId !== undefined &&
    cleaned.duplicateOfId !== null &&
    typeof cleaned.duplicateOfId !== "string"
  )
    return "duplicateOfId must be an id or null";
  const reviewer = personReason("reviewer", cleaned.reviewer);
  if (reviewer) return reviewer;
  for (const key of ["plannedStart", "plannedEnd"] as const) {
    const value = cleaned[key];
    if (value !== undefined && value !== null && !DATE_RE.test(String(value)))
      return `${key} must be a YYYY-MM-DD date or null`;
  }
  for (const key of TRACKING_BOOL_KEYS) {
    const value = cleaned[key];
    if (value !== undefined && typeof value !== "boolean") return `${key} must be a boolean`;
  }
  for (const key of TRACKING_TEXT_KEYS) {
    const bad = textReason(key, cleaned[key] === undefined ? null : cleaned[key], TEXT_MAX);
    if (bad) return bad;
  }
  /* IPM3: disposition and projectId are recorded together or not at all —
     judged on the record this patch would PRODUCE, not the patch alone, so
     a patch that only touches one field is still valid when `current`
     already carries the other. */
  const merged = { ...(current ?? EMPTY_TRACKING), ...cleaned };
  if (merged.disposition === "duplicate_of") {
    /* UF18: a duplicate pairs with the effort it duplicates, never a project */
    if (merged.duplicateOfId === null) return "duplicate_of needs the duplicateOfId it duplicates";
    if (merged.projectId !== null) return "a duplicate maps to another effort, not a project";
  } else {
    if ((merged.disposition === null) !== (merged.projectId === null))
      return "disposition and projectId must be set or cleared together";
    if (merged.duplicateOfId !== null) return "duplicateOfId is only set with disposition duplicate_of";
  }
  if (merged.disposition === "team_builds_platform_reviews" && merged.reviewer === null)
    return REVIEWER_REQUIRED_REASON;
  /* per Lewis, Sep 2026: an ungroomed ask can't be given a disposition —
     sizing/scoring comes first. Clearing a disposition is always allowed
     regardless of groomed status (merged.disposition === null skips this). */
  if (merged.disposition !== null && merged.groomed !== true) return UNGROOMED_DISPOSITION_REASON;
  /* FR3: setting Rejected, or clearing the reason of a rejected effort,
     needs the reason on the record this patch would produce */
  const touchesRejection =
    cleaned.platformStatus === "rejected" ||
    (cleaned.platformNotes !== undefined && merged.platformStatus === "rejected");
  if (touchesRejection && (merged.platformNotes ?? "").trim() === "") return REJECTION_REASON_REQUIRED;
  return null;
}

/* the correction appends onto the standing record — or onto the honest
   empty when nobody has triaged yet */
export const applyTrackingPatch = (
  current: CapabilityTracking | null,
  patch: TrackingPatch,
  by: UserRef,
  date: string,
): CapabilityTracking => ({
  ...(current ?? EMPTY_TRACKING),
  ...cleanTrackingPatch(patch),
  recorded: { source: ENTERED_SRC, date, by },
});

/* ── scoring + dedup, ported verbatim (IDM8) ── */

/* Reach-weighted priority score — a lightweight WSJF: value × reach ÷
   effort. Exact constants from perception-intake/src/domain/score.ts. */
export interface Scorable {
  priority: string | null;
  scope: string | null;
  stakeholderCount: number;
  effort: string | null;
}

const VALUE: Record<string, number> = { must: 4, should: 3, could: 2, wont: 0 };
const WEIGHT: Record<string, number> = { s: 1, m: 2, l: 4, xl: 8 };

/* submitter + stakeholders, with a nudge for anything flagged common */
export const reach = (f: Scorable): number =>
  1 + f.stakeholderCount + (f.scope === "common" ? 1 : 0);

/* higher = do sooner; effort defaults to M so a score always shows */
export function computeScore(f: Scorable): number {
  const value = VALUE[f.priority ?? ""] ?? 1;
  const weight = WEIGHT[f.effort ?? ""] ?? WEIGHT.m!;
  return Math.round(((value * reach(f)) / weight) * 10) / 10;
}

/* Dedup text similarity — exact port of the prototype's tokenize/jaccard
   (stop-list, >2-char tokens, name counted twice, +0.1 same-area bonus
   capped at 1, threshold 0.22, top 5). */
const STOP = new Set([
  "the", "a", "an", "and", "or", "of", "for", "to", "on", "in", "is", "it", "its",
  "with", "at", "by", "this", "that", "be", "as", "we", "our", "need", "needs",
  "detection", "detect",
]);

const tokenize = (text: string): Set<string> =>
  new Set(
    text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, " ")
      .split(" ")
      .filter((t) => t.length > 2 && !STOP.has(t)),
  );

const jaccard = (a: Set<string>, b: Set<string>): number => {
  if (a.size === 0 || b.size === 0) return 0;
  let inter = 0;
  for (const t of a) if (b.has(t)) inter++;
  return inter / (a.size + b.size - inter);
};

export interface SimilarityQuery {
  name: string;
  description: string | null;
}

export function similarityRanked(
  query: SimilarityQuery,
  pool: readonly Capability[],
): { capability: Capability; score: number }[] {
  const input = tokenize(`${query.name} ${query.name} ${query.description ?? ""}`);
  if (input.size === 0) return [];
  return pool
    .filter((c) => !c.archived)
    .map((c) => {
      const cand = tokenize(`${c.name} ${c.name} ${c.description ?? ""}`);
      const score = jaccard(input, cand);
      return { capability: c, score: Math.min(score, 1) };
    })
    .filter((x) => x.score >= 0.22)
    .sort((a, b) => b.score - a.score)
    .slice(0, 5);
}

/* One board row (intake-dashboard.md ID2): a capability with the reach,
   tracking summary, and score the dashboard ranks by. */
export interface CapabilityBoardRow {
  capability: Capability;
  stakeholderCount: number;
  tracking: CapabilityTracking | null;
  score: number;
}

/* The whitelist an edit may speak — unknown request-body keys and explicit
   `undefined` values are DROPPED, never merged into the record. */
const EDIT_KEYS = [
  "teamId",
  "name",
  "description",
  "pointOfContact",
  "productId",
  "vehicleId",
  "scope",
  "subStatus",
  "priority",
  "neededBy",
  "phaseTarget",
  "jamaStatus",
  "jamaLink",
  "dependencies",
  "owners",
  "archived",
] as const satisfies readonly (keyof (CapabilityInput & { archived: boolean }))[];

const TRIMMED_KEYS = ["name", "description", "jamaLink", "dependencies"] as const;

/* keep only known, defined keys; trim what people type (edits get the same
   tidy-up creates do) */
export function cleanCapabilityEdit(edit: CapabilityEdit): CapabilityEdit {
  const out: Record<string, unknown> = {};
  for (const key of EDIT_KEYS) {
    const value = (edit as Record<string, unknown>)[key];
    if (value !== undefined) out[key] = value;
  }
  for (const key of TRIMMED_KEYS) if (typeof out[key] === "string") out[key] = (out[key] as string).trim();
  return out as CapabilityEdit;
}

/* An edit validates as the MERGED record (IDM4): every rule above holds
   after the correction, and an edit that says nothing — empty, unknown
   keys only, or all-undefined — is refused as a non-statement. */
export function validateCapabilityEdit(
  edit: CapabilityEdit,
  current: Capability,
  teams: CanonicalTeam[],
): string | null {
  const cleaned = cleanCapabilityEdit(edit);
  if (Object.keys(cleaned).length === 0) return "nothing to change";
  if (cleaned.archived !== undefined && typeof cleaned.archived !== "boolean")
    return "archived must be a boolean";
  const { archived: _archived, ...fields } = cleaned;
  const merged: CapabilityInput = { ...toInput(current), ...fields };
  return validateCapabilityInput(merged, teams);
}

const toInput = (c: Capability): CapabilityInput => ({
  teamId: c.teamId,
  name: c.name,
  description: c.description,
  pointOfContact: c.pointOfContact ?? null,
  productId: c.productId ?? null,
  vehicleId: c.vehicleId ?? null,
  scope: c.scope,
  subStatus: c.subStatus,
  priority: c.priority ?? null,
  neededBy: c.neededBy,
  phaseTarget: c.phaseTarget ?? null,
  jamaStatus: c.jamaStatus ?? null,
  jamaLink: c.jamaLink,
  dependencies: c.dependencies,
  owners: c.owners ?? null,
});

/* the route's tidy-up before storing — trims what people type, and keeps
   ONLY the shared fields (UF2): a stale client still sending domain / area /
   domainDetail is not refused, but nothing it sends outside the record's own
   keys is stored either, so a new ask never carries the legacy trio */
export const cleanCapabilityInput = (input: CapabilityInput): CapabilityInput => ({
  teamId: input.teamId,
  name: input.name.trim(),
  description: input.description === null ? null : input.description.trim(),
  pointOfContact:
    input.pointOfContact == null
      ? null
      : { userId: input.pointOfContact.userId.trim(), name: input.pointOfContact.name.trim() },
  productId: input.productId ?? null,
  vehicleId: input.vehicleId ?? null,
  scope: input.scope,
  subStatus: input.subStatus,
  priority: input.priority ?? null,
  neededBy: input.neededBy,
  phaseTarget: input.phaseTarget ?? null,
  jamaStatus: input.jamaStatus ?? null,
  jamaLink: input.jamaLink === null ? null : input.jamaLink.trim(),
  dependencies: input.dependencies === null ? null : input.dependencies.trim(),
  /* absent stays absent — "nobody asked" must not become an explicit null */
  ...(input.owners !== undefined ? { owners: input.owners } : {}),
});

/* ── the satellites (intake-data-model.md entity map, PR-5) ── */

export const LINK_TYPES = [
  { value: "related", label: "Related" },
  { value: "variant", label: "Variant" },
  { value: "duplicate", label: "Duplicate" },
] as const satisfies readonly VocabOption[];

/* a team's +1 (HR-A4): the reach signal. Keyed by (capability, team) —
   the latest revision per pair is the truth, a tombstone is a withdrawal. */
export interface Stakeholder {
  teamId: string;
  note: string | null;
  recorded: Stamp;
}

export interface CapabilityLink {
  otherId: string;
  linkType: string;
  recorded: Stamp;
}

export interface CapabilityComment {
  id: string;
  body: string;
  recorded: Stamp;
}

/* One effort's filing (intake-input-path.md IP6): revision 1's stamp —
   who filed it, when, and through which path — under the CURRENT name,
   because that is the name a reader recognises it by. */
export interface CapabilityFiling {
  capabilityId: string;
  name: string;
  teamId: string;
  archived: boolean;
  recorded: Stamp;
}

/* What remains of a deleted effort (designs/intake-delete.md ID4): which,
   who, when, why — never the deleted content itself. */
export interface CapabilityDeletion {
  capabilityId: string;
  reason: string;
  recorded: Stamp;
}

/* The typed name + reason alone (intake-delete.md ID3). Separate so the
   route answers an unconfirmed request BEFORE loading any dependents: an
   invalid request must not pay the every-effort scan, and a failure in
   that scan must not turn a 400 into a 502 (Copilot on #138). */
export function capabilityDeleteConfirmationReason(
  current: Pick<Capability, "name">,
  confirmName: unknown,
  reason: unknown,
): { status: 400; error: string } | null {
  if (typeof confirmName !== "string" || confirmName.trim() !== current.name)
    return { status: 400, error: `to delete, type the effort's name exactly: "${current.name}"` };
  if (typeof reason !== "string" || reason.trim() === "")
    return { status: 400, error: "a reason is required — it is what the deletion receipt keeps" };
  return null;
}

/* Whether an Admin may delete this effort (intake-delete.md ID2/ID3). Delete
   is the one exception to append-only, so it is fenced to MISTAKES: nothing
   else may rely on the effort. The confirmation is answered FIRST, so a
   wrong name learns nothing about the effort's dependents. The caller has
   already checked the Admin role and that the effort exists. */
export function capabilityDeleteReason(d: {
  current: Capability;
  confirmName: unknown;
  reason: unknown;
  links: readonly CapabilityLink[];
  /* ids of other efforts whose grooming names this one as `duplicateOfId` */
  foldedIn: readonly string[];
  tracking: Pick<CapabilityTracking, "projectId"> | null;
  stakeholders: readonly Stakeholder[];
}): { status: 400 | 409; error: string } | null {
  const instead = "archive it instead if it should only be hidden";
  const unconfirmed = capabilityDeleteConfirmationReason(d.current, d.confirmName, d.reason);
  if (unconfirmed) return unconfirmed;
  if (d.links.length > 0)
    return {
      status: 409,
      error:
        `this effort is linked to ${d.links.map((l) => l.otherId).join(", ")} — deleting it would ` +
        `change those records; ${instead}`,
    };
  if (d.foldedIn.length > 0)
    return {
      status: 409,
      error: `${d.foldedIn.join(", ")} was folded into this effort as a duplicate; ${instead}`,
    };
  if (d.tracking?.projectId != null)
    return {
      status: 409,
      error: `this effort is mapped to project ${d.tracking.projectId}; remove the mapping first, or ${instead}`,
    };
  if (d.stakeholders.length > 0)
    return {
      status: 409,
      error:
        `${d.stakeholders.map((s) => s.teamId).join(", ")} backed this effort, so it is not a ` +
        `mistake to delete — withdraw that +1 first if it was itself the mistake, or ${instead}`,
    };
  return null;
}

const NOTE_MAX = 2000;

/* the +1's rules: a real current team, not the ask's own team (the
   submitter is already reach 1), and one live +1 per team */
export function validateStakeholderInput(
  teamId: string,
  note: string | null,
  teams: CanonicalTeam[],
  capability: Capability,
  standing: Stakeholder[],
): string | null {
  const team = teams.find((t) => t.id === teamId);
  if (!team) return `unknown team "${teamId}" — pick from the canonical list`;
  if (team.retired) return `team "${team.name}" is retired — pick a current team`;
  if (teamId === capability.teamId)
    return `"${team.name}" submitted this capability — the submitter is already counted`;
  if (standing.some((s) => s.teamId === teamId))
    return `"${team.name}" is already a stakeholder`;
  return textReason("note", note, NOTE_MAX);
}

export const validateCommentBody = (body: unknown): string | null =>
  typeof body !== "string" || !body.trim()
    ? "a comment needs a body"
    : body.length > TEXT_MAX
      ? `body is over ${TEXT_MAX} characters`
      : null;

export function validateLinkInput(
  capabilityId: string,
  otherId: unknown,
  linkType: unknown,
  standing: CapabilityLink[],
): string | null {
  if (!has(LINK_TYPES, linkType)) return "unknown linkType";
  if (typeof otherId !== "string" || !otherId) return "otherId is required";
  if (otherId === capabilityId) return "a capability cannot link to itself";
  if (standing.some((l) => l.otherId === otherId)) return "these capabilities are already linked";
  return null;
}

/* the correction is a NEW revision carrying the actor's stamp (IDM4) —
   applied through the same whitelist-and-trim the validator saw, so an
   internal caller can't smuggle unknown props or undefined overwrites */
export const applyCapabilityEdit = (
  current: Capability,
  edit: CapabilityEdit,
  by: UserRef,
  date: string,
): Capability => {
  const next: Capability = {
    ...current,
    ...cleanCapabilityEdit(edit),
    id: current.id,
    recorded: { source: ENTERED_SRC, date, by },
  };
  /* false stores as ABSENT ("absent means live"), so both stores serialize
     identically whichever wrote the revision */
  if (!next.archived) delete next.archived;
  return next;
};
